import type { DashboardTool, RecommendedTool, ToolCustomization } from '@magicschool/business-logic/tools';
import { logger } from '@magicschool/logger';
import { toast } from '@magicschool/spellbook';
import type { CreateNewToolCustomization, Json, Tables } from '@magicschool/supabase/types';
import { createStoreSlice, type SetField } from 'features/store/zustand';
import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import type { IntlShape } from 'react-intl';
import {
  createNewToolCustomizationServerAction,
  getToolCustomizationByShareCodeServerAction,
  revalidateToolsServerAction,
  type ToolCustomizationShareResponse,
  updateToolCustomizationServerAction,
  updateToolCustomizationSharing,
} from '@/app/(teacher)/tools/actions';
import { analyticsTrack } from '@/util/amplitude/analyticsClient';
import type { ServerActionResponse } from '../server/actions/types';

export type ToolsSortType = 'popular' | 'newest' | 'az' | 'za';
export type ToolsStore = ToolsData & ToolsActions;

export type ToolsData = {
  categories: Tables<'categories'>[];
  copyToolName: string;
  deleteToolModalOpen: boolean;
  hasEnterpriseTools: boolean;
  loadingCustomTool: boolean;
  loadingShareTool: boolean;
  promoteToolModalOpen: boolean;
  promotingCustomTool: boolean;
  recommendedTools: RecommendedTool[];
  searchResults: string[];
  searchTerm: string;
  selectedCategory: string;
  shareDrawerLoading: boolean;
  shareDrawerOpen: boolean;
  shareToolModalOpen: boolean;
  sortType: ToolsSortType;
  tempCustomizationStatus: ToolCustomization['status'];
  toolCustomizationToEdit: ToolCustomization | null;
  tools: DashboardTool[];
  toolToShareResponse: ServerActionResponse<ToolCustomizationShareResponse> | null;
  favoritesModalOpen: boolean;
  userFavorites: string[];

  deleteCustomTool: (toolCustomizationId: string, intl: IntlShape, router: AppRouterInstance) => void;
  saveToolCustomization: (intl: IntlShape) => Promise<string | undefined>;
  openShareToolModal: (toolCustomizationId: string) => void;
  copyTool: (intl: IntlShape, router: AppRouterInstance) => void;
  closeShareToolModal: (router: AppRouterInstance) => void;
  openShareToolDrawer: (customization: ToolCustomization) => void;
  closeShareToolDrawer: () => void;
  resetShareToolDrawer: () => void;
  updateShareLinkAccessType: (
    intl: IntlShape,
    accessType: 'public' | 'private' | 'org',
    location?: 'CustomTool' | 'CustomStudentTool',
  ) => void;
  hydrate: (args: {
    categories: ToolsData['categories'];
    tools: ToolsData['tools'];
    hasEnterpriseTools: ToolsData['hasEnterpriseTools'];
    userFavorites: ToolsData['userFavorites'];
    recommendedTools: ToolsData['recommendedTools'];
  }) => void;
};

const defaultState = {
  categories: [],
  copyToolName: '',
  deleteToolModalOpen: false,
  hasEnterpriseTools: false,
  loadingCustomTool: false,
  loadingShareTool: false,
  promoteToolModalOpen: false,
  promotingCustomTool: false,
  recommendedTools: [],
  searchResults: [],
  searchTerm: '',
  selectedCategory: '',
  shareDrawerLoading: false,
  shareDrawerOpen: false,
  shareToolModalOpen: false,
  sortType: 'popular' as const,
  tempCustomizationStatus: 'pending' as const,
  toolCustomizationToEdit: null,
  tools: [],
  toolToShareResponse: null,
  favoritesModalOpen: false,
  userFavorites: [],
};

export type ToolsActions = {
  setField: SetField<ToolsStore>;
};

export type CustomToolSharingAccessType = 'public' | 'private' | 'org';

export const createToolsPageStoreSlice = createStoreSlice(
  'ToolsStoreData',
  structuredClone(defaultState),
  ({ setField, set, get, getFull }) => ({
    setField,
    hydrate: (args) => set({ ...structuredClone(defaultState), ...args }),
    saveToolCustomization: async (intl) => {
      const {
        tempToolCustomization,
        toolToCustomize,
        validateFields,
        isNewTool,
        setField: setCustomToolField,
        toolApplication,
      } = getFull().ToolCustomizationStoreData;
      if (!tempToolCustomization || !toolToCustomize) return;

      const isEdit = !isNewTool && !!tempToolCustomization.id;

      const fieldsValid = validateFields(toolToCustomize, tempToolCustomization);
      if (!fieldsValid) {
        setCustomToolField('invalidStateModalOpen')(true);
        return;
      }

      if (isEdit) {
        // update the existing tool
        const updated = await updateToolCustomizationServerAction(tempToolCustomization.id, {
          id: tempToolCustomization.id,
          json_config: tempToolCustomization.json_config as Json,
        });
        getFull().ToolCustomizationStoreData.discardToolChanges();
        if (updated.error) {
          toast({ title: intl.formatMessage({ id: 'new-custom-tool.error' }), status: 'error' });
          throw new Error(updated.error.message);
        }
        toast({ title: intl.formatMessage({ id: 'edit-custom-tool.success' }), status: 'success' });
        return updated.data?.id;
      }

      let newCustomization: ServerActionResponse<ToolCustomization>;
      const tool_application = toolApplication || 'org';
      const status = tool_application === 'org' ? 'pending' : 'active';
      if (toolToCustomize.tool.assistant_tool_type) {
        newCustomization = await updateToolCustomizationServerAction(tempToolCustomization.id, {
          tool_slug: tempToolCustomization.tool_slug,
          tool_uuid: tempToolCustomization.tool_uuid,
          json_config: tempToolCustomization.json_config as Json,
          tool_application,
          status,
        });
      } else {
        newCustomization = await createNewToolCustomizationServerAction({
          tool_slug: tempToolCustomization.tool_slug,
          tool_uuid: tempToolCustomization.tool_uuid,
          json_config: tempToolCustomization.json_config,
          tool_application: toolApplication || 'individual',
          status,
          source_customization_id: tempToolCustomization.id || null,
        });
      }

      if (newCustomization.error) {
        toast({ title: intl.formatMessage({ id: 'new-custom-tool.error' }), status: 'error' });
        throw new Error(newCustomization.error.message);
      }
      if (toolApplication === 'org' && isNewTool) {
        setCustomToolField('customizingStep')('access');
        set({ tempCustomizationStatus: 'pending', toolCustomizationToEdit: newCustomization.data });
      }

      return newCustomization.data?.id;
    },
    deleteCustomTool: async (toolCustomizationId, intl, router) => {
      set({ loadingCustomTool: true });
      const response = await fetch(`/api/tool_customizations/${toolCustomizationId}`, { method: 'DELETE' });
      if (!response.ok) {
        toast({ title: intl.formatMessage({ id: 'delete-custom-tool.error' }), status: 'error' });
        return;
      }
      const tools = get().tools;
      set({
        tools: tools.filter((tool) => tool.toolCustomizationId !== toolCustomizationId),
        deleteToolModalOpen: false,
        loadingCustomTool: false,
      });
      await revalidateToolsServerAction();
      router.push('/tools');
      analyticsTrack('CustomToolChat:DeleteTool', {
        state: 'confirmed',
        location: 'ToolChat',
      });
      toast({ title: intl.formatMessage({ id: 'delete-custom-tool.success' }), status: 'success' });
    },
    openShareToolModal: async (toolCustomizationId) => {
      set({ toolToShareResponse: null, copyToolName: '' });
      set({ shareToolModalOpen: true, loadingShareTool: true });
      const fetchedTool = await getToolCustomizationByShareCodeServerAction(toolCustomizationId);
      set({ toolToShareResponse: fetchedTool, loadingShareTool: false, copyToolName: fetchedTool.data?.title });
    },
    closeShareToolModal: (router) => {
      set({ shareToolModalOpen: false });
      router.push('/tools');
    },
    copyTool: async (intl, router) => {
      set({ loadingShareTool: true });
      const { toolToShareResponse, copyToolName, closeShareToolModal } = get();
      const toolData = toolToShareResponse?.data;
      if (!toolData) {
        return;
      }
      const newToolCustomization: CreateNewToolCustomization = {
        tool_slug: toolData.toolSlug,
        tool_uuid: toolData.toolId,
        json_config: { ...toolData.toolCustomization.json_config, tool_title: copyToolName },
        tool_application: 'individual',
        source_customization_id: toolData.toolCustomization.id || null,
      };
      const response = await createNewToolCustomizationServerAction(newToolCustomization);
      if (response.error) {
        toast({ title: intl.formatMessage({ id: 'share-tool.error' }), status: 'error' });
        closeShareToolModal(router);
        return;
      }

      toast({ title: intl.formatMessage({ id: 'share-tool.success' }), status: 'success' });
      analyticsTrack('CustomTool:AddedSharedTool', {
        tool_slug: toolData.toolSlug,
        tool_customization_id: toolData.toolCustomizationId,
        source_teacher: toolData.toolCustomization.teacher_id,
        source_share_type: toolData.toolCustomization.sharing_access_type,
      });
      await revalidateToolsServerAction();
      closeShareToolModal(router);
    },
    openShareToolDrawer: (customization) => {
      const existing = get().toolCustomizationToEdit;
      // Prefer store sharing fields; page props stay stale after updates.
      const toolCustomizationToEdit =
        existing?.id === customization.id
          ? {
              ...customization,
              sharing_access_type: existing.sharing_access_type,
              sharing_code: existing.sharing_code,
            }
          : customization;
      set({ shareDrawerOpen: true, toolCustomizationToEdit, shareDrawerLoading: false });
    },
    closeShareToolDrawer: () => {
      set({ shareDrawerOpen: false });
    },
    resetShareToolDrawer: () => {
      set({ toolCustomizationToEdit: null, shareDrawerLoading: false });
    },
    updateShareLinkAccessType: async (intl, accessType: 'public' | 'private' | 'org', location = 'CustomTool') => {
      const { toolCustomizationToEdit } = get();
      if (!toolCustomizationToEdit?.id) return;
      set({ shareDrawerLoading: true });
      analyticsTrack('CustomEducatorTool:UpdateShareLinkAccessType', {
        location,
        accessType,
        toolCustomizationId: toolCustomizationToEdit.id,
      });

      const result = await updateToolCustomizationSharing(toolCustomizationToEdit.id, accessType);
      if (result.error || !result.data) {
        logger.error(result?.error?.message || `Error in updating sharing link for tool_customization.id: ${toolCustomizationToEdit.id}`, {
          error: result.error,
        });
        toast({ title: intl.formatMessage({ id: 'update-share-tool.error' }), status: 'error' });
        // Leave drawer open so the user can retry.
        set({ shareDrawerLoading: false });
        return;
      }

      set({ shareDrawerLoading: false, toolCustomizationToEdit: result.data });
    },
  }),
);
